import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  getClientById,
  getClientOrders,
  getClientCreditHistory,
  recordClientPayment,
} from "../../Services/ClientApi";
import { toast } from "react-toastify";
import OrderDetailsModal from "../OrderDetails";
import { formatdate, formatDateAndTime } from "../../Helpers/Helpers";
import { updateCashJournal } from "../../Services/JournalApi";
import { useUser } from "../../Auth/useUser";

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toFixed(3);

const Badge = ({ children, color = "#1976d2", bg = "#e3f2fd" }) => (
  <span
    style={{
      backgroundColor: bg,
      color,
      borderRadius: 4,
      padding: "2px 8px",
      fontSize: 11,
      fontWeight: 700,
    }}
  >
    {children}
  </span>
);

// ── Tab panels ────────────────────────────────────────────────────────────────
const InfoTab = ({ client }) => {
  if (!client) return null;
  const rows = [
    ["Code", client.code],
    ["Nom", client.name],
    ["Email", client.email || "—"],
    ["Téléphone", client.phone || "—"],
    ["Adresse", client.address || "—"],
    ["Matricule fiscal", client.taxNumber || "—"],
    ["Code ERP", client.code_erp || "—"],
    ["Note", client.note || "—"],
    ["Crédit en cours", `${fmt(client.credit)} TND`],
    [
      "Limite crédit",
      client.creditLimit != null ? `${fmt(client.creditLimit)} TND` : "Aucune",
    ],
    ["Créé le", formatdate(client.createdAt)],
  ];

  return (
    <Box>
      {/* Status badges */}
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        {client.isDefault && (
          <Badge color="#856404" bg="#fff3cd">
            Par défaut
          </Badge>
        )}
        {client.isBlocked ? (
          <Badge color="#721c24" bg="#f8d7da">
            Bloqué
          </Badge>
        ) : (
          <Badge color="#155724" bg="#d4edda">
            Actif
          </Badge>
        )}
        {client.credit > 0 && (
          <Badge color="#e65100" bg="#fff3e0">
            Crédit: {fmt(client.credit)} TND
          </Badge>
        )}
      </Box>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label} style={{ borderBottom: "1px solid #f0f0f0" }}>
              <td
                style={{
                  padding: "8px 12px",
                  color: "#666",
                  width: "40%",
                  fontSize: 13,
                }}
              >
                {label}
              </td>
              <td
                style={{ padding: "8px 12px", fontWeight: 500, fontSize: 13 }}
              >
                {value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

const OrdersTab = ({ clientId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedCart, setSelectedCart] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const limit = 10;

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const res = await getClientOrders(clientId, { page: p, limit });
      if (res.data.success && res.data.data) {
        setSessions(res.data.data.allSessions || []);
        setPagination(res.data.data.pagination || {});
      }
    } catch {
      toast.error("Erreur chargement commandes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
  }, [page]);

  const handleRowClick = (sessionItem) => {
    // Shape the cart prop exactly as Orders.jsx does it
    const firstCart = sessionItem.carts[0] || {};
    const cartForModal = {
      ...firstCart,
      session: sessionItem.session,
      payments: sessionItem.payments || [],
      tableNumber:
        sessionItem.session?.table_id?.table_number ||
        sessionItem.session?.table_id?.numero ||
        firstCart.table_id?.numero,
    };
    setSelectedCart(cartForModal);
    setDetailOpen(true);
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );

  if (!sessions.length)
    return (
      <Box textAlign="center" py={4} color="text.secondary">
        <i className="fas fa-receipt" style={{ fontSize: 32, opacity: 0.3 }} />
        <Typography mt={1}>Aucune commande trouvée</Typography>
      </Box>
    );

  return (
    <Box>
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f8f9fa" }}>
            <th style={th}>Code</th>
            <th style={th}>Date</th>
            <th style={{ ...th, textAlign: "right" }}>Total</th>
            <th style={th}>Statut</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((item) => (
            <tr
              key={item.session._id}
              style={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
              onClick={() => handleRowClick(item)}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <td style={{ ...td, fontWeight: 600, fontFamily: "monospace" }}>
                {item.session.code || "—"}
              </td>
              <td style={td}>{formatDateAndTime(item.session.createdAt)}</td>
              <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>
                {fmt(item.totalAmount)} TND
              </td>
              <td style={td}>
                <Badge
                  color={
                    item.session.status === "inactive" ? "#155724" : "#856404"
                  }
                  bg={
                    item.session.status === "inactive" ? "#d4edda" : "#fff3cd"
                  }
                >
                  {item.session.status === "inactive" ? "Payée" : "En cours"}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
          mt={2}
        >
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ‹ Préc
          </button>
          <span style={{ fontSize: 13, color: "#666" }}>
            Page {page} / {pagination.totalPages} — {pagination.totalItems}{" "}
            commande(s)
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={page === pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Suiv ›
          </button>
        </Box>
      )}

      {/* Reuse the same OrderDetailsModal */}
      <OrderDetailsModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        cart={selectedCart}
        currency="TND"
      />
    </Box>
  );
};

const CreditTab = ({ clientId, client, onPaymentSuccess }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [paying, setPaying] = useState(false);
  const limit = 10;
  const user = useUser();
  const infoUser = user.result;

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const res = await getClientCreditHistory(clientId, { page: p, limit });
      if (res.data.success && res.data.data) {
        setEntries(res.data.data.data);
        setPagination(res.data.data.pagination);
      }
    } catch {
      toast.error("Erreur chargement historique crédit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
  }, [page]);

  const handlePayment = async () => {
    const amount = parseFloat(payAmount);
    if (!amount || amount <= 0) {
      toast.error("Montant invalide.");
      return;
    }
    if (amount > (client?.credit || 0)) {
      toast.error(`Montant supérieur au solde (${fmt(client?.credit)} TND).`);
      return;
    }
    const activeJournal = JSON.parse(localStorage.getItem("activeJournal"));
    if (!activeJournal) {
      toast.error(
        "Aucune caisse ouverte. Impossible d'enregistrer le paiement.",
      );
      return;
    }

    try {
      setPaying(true);

      // 1. Decrease client credit + write history
      await recordClientPayment(clientId, {
        amount,
        payment_method: payMethod,
        journal_id: activeJournal._id || activeJournal.id,
        organisation_id: activeJournal.organisation_id,
        note: `Paiement crédit (${payMethod}) — caissier: ${infoUser?.nom || infoUser?.name || infoUser?.id}`,
        created_by: infoUser?.id,
      });

      // 2. Update fond courant only for cash payments
      if (payMethod === "cash") {
        const journalId = activeJournal.id || activeJournal._id;
        const currentFond = Number(activeJournal.fond_courant || 0);
        const res = await updateCashJournal(journalId, {
          fond_courant: currentFond + amount,
        });
        if (res.data.success) {
          localStorage.removeItem("activeJournal");
          localStorage.setItem("activeJournal", JSON.stringify(res.data.data));
        }
      }

      toast.success(`Paiement de ${fmt(amount)} TND enregistré avec succès.`);
      setPayAmount("");
      load(1); // refresh history
      if (onPaymentSuccess) onPaymentSuccess(); // refresh client balance in parent
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.message || err.message));
    } finally {
      setPaying(false);
    }
  };

  const typeConfig = {
    debit: {
      label: "Achat à crédit",
      color: "#721c24",
      bg: "#f8d7da",
      sign: "+",
    },
    payment: { label: "Paiement", color: "#155724", bg: "#d4edda", sign: "−" },
    refund: {
      label: "Remboursement",
      color: "#0c5460",
      bg: "#d1ecf1",
      sign: "−",
    },
    adjustment: {
      label: "Ajustement",
      color: "#856404",
      bg: "#fff3cd",
      sign: "±",
    },
  };

  const balance = client?.credit || 0;

  return (
    <Box>
      {/* ── Payment panel ── */}
      {balance > 0 && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: "1px solid #ffccbc",
            borderRadius: "10px",
            backgroundColor: "#fff8f5",
          }}
        >
          {/* Balance summary */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography fontWeight={700} fontSize={14}>
              Solde à régler
            </Typography>
            <Typography fontWeight={800} fontSize={18} color="#e65100">
              {fmt(balance)} TND
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Method selector */}
          <Box display="flex" gap={1} mb={2}>
            {[
              { value: "cash", label: "Espèces", icon: "fa-money-bill-wave" },
              { value: "creditcard", label: "Carte", icon: "fa-credit-card" },
              { value: "cheque", label: "Chèque", icon: "fa-money-check" },
            ].map((m) => (
              <button
                key={m.value}
                onClick={() => setPayMethod(m.value)}
                className={`btn btn-sm ${payMethod === m.value ? "btn-primary" : "btn-outline-secondary"}`}
                style={{ flex: 1, fontSize: 12 }}
              >
                <i className={`fas ${m.icon} mr-1`} />
                {m.label}
              </button>
            ))}
          </Box>

          {/* Amount input + confirm */}
          <Box display="flex" gap={1} alignItems="center">
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder={`Montant (max ${fmt(balance)})`}
              value={payAmount}
              min="0"
              max={balance}
              step="0.001"
              onChange={(e) => setPayAmount(e.target.value)}
              style={{ flex: 1 }}
            />
            <button
              className="btn btn-sm btn-success"
              onClick={handlePayment}
              disabled={paying || !payAmount || parseFloat(payAmount) <= 0}
              style={{ whiteSpace: "nowrap", minWidth: 120 }}
            >
              {paying ? (
                <span className="spinner-border spinner-border-sm mr-1" />
              ) : (
                <i className="fas fa-check mr-1" />
              )}
              Enregistrer
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPayAmount(fmt(balance))}
              title="Tout régler"
              style={{ whiteSpace: "nowrap" }}
            >
              Tout
            </button>
          </Box>
        </Box>
      )}

      {/* ── History table ── */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : !entries.length ? (
        <Box textAlign="center" py={4} color="text.secondary">
          <i
            className="fas fa-history"
            style={{ fontSize: 32, opacity: 0.3 }}
          />
          <Typography mt={1}>Aucun mouvement de crédit</Typography>
        </Box>
      ) : (
        <>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa" }}>
                <th style={th}>Date</th>
                <th style={th}>Type</th>
                <th style={{ ...th, textAlign: "right" }}>Montant</th>
                <th style={{ ...th, textAlign: "right" }}>Solde après</th>
                <th style={th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const cfg = typeConfig[entry.type] || {
                  label: entry.type,
                  color: "#333",
                  bg: "#eee",
                  sign: "",
                };
                return (
                  <tr
                    key={entry._id || entry.id}
                    style={{ borderBottom: "1px solid #f0f0f0" }}
                  >
                    <td style={td}>
                      {formatDateAndTime(entry.date || entry.createdAt)}
                    </td>
                    <td style={td}>
                      <Badge color={cfg.color} bg={cfg.bg}>
                        {cfg.label}
                      </Badge>
                    </td>
                    <td
                      style={{
                        ...td,
                        textAlign: "right",
                        fontWeight: 600,
                        color: entry.amount > 0 ? "#c62828" : "#2e7d32",
                      }}
                    >
                      {cfg.sign} {fmt(Math.abs(entry.amount))} TND
                    </td>
                    <td style={{ ...td, textAlign: "right", color: "#666" }}>
                      {fmt(entry.balance_after)} TND
                    </td>
                    <td style={{ ...td, color: "#888", fontSize: 12 }}>
                      {entry.note || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              mt={2}
            >
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ‹ Préc
              </button>
              <span style={{ fontSize: 13, color: "#666" }}>
                Page {page} / {pagination.pages} — {pagination.total}{" "}
                mouvement(s)
              </span>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === pagination.pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suiv ›
              </button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

// shared cell styles
const th = {
  padding: "8px 12px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: 12,
  color: "#555",
  borderBottom: "2px solid #dee2e6",
};
const td = { padding: "8px 12px", verticalAlign: "top" };

// ── Main modal ────────────────────────────────────────────────────────────────
const ClientDetailModal = ({ open, onClose, clientId, initialTab = 0 }) => {
  const [tab, setTab] = useState(initialTab);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && clientId) {
      setTab(initialTab);
      setClient(null);
      loadClient();
    }
  }, [open, clientId, initialTab]);

  const loadClient = async () => {
    setLoading(true);
    try {
      const res = await getClientById(clientId);
      if (res.data.success && res.data.data) {
        setClient(res.data.data);
      }
    } catch {
      toast.error("Erreur chargement client");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          height: "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#1976d2",
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          {loading || !client ? (
            <Typography variant="h6" color="#fff" fontWeight={700}>
              Chargement...
            </Typography>
          ) : (
            <>
              <Typography variant="h6" color="#fff" fontWeight={700}>
                {client.name}
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.75)">
                {client.code}
              </Typography>
            </>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <i className="fas fa-times" />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Box
        sx={{ borderBottom: "1px solid #dee2e6", backgroundColor: "#f8f9fa" }}
      >
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 44 }}>
          <Tab
            label={
              <span>
                <i className="fas fa-user mr-1" />
                Informations
              </span>
            }
            sx={{ textTransform: "none", minHeight: 44 }}
          />
          <Tab
            label={
              <span>
                <i className="fas fa-receipt mr-1" />
                Commandes
              </span>
            }
            sx={{ textTransform: "none", minHeight: 44 }}
          />
          <Tab
            label={
              <span>
                <i className="fas fa-history mr-1" />
                Historique crédit
              </span>
            }
            sx={{ textTransform: "none", minHeight: 44 }}
          />
        </Tabs>
      </Box>

      {/* Content */}
      <DialogContent sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tab === 0 && <InfoTab client={client} />}
            {tab === 1 && <OrdersTab clientId={clientId} />}
            {tab === 2 && (
              <CreditTab
                clientId={clientId}
                client={client}
                onPaymentSuccess={loadClient}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientDetailModal;
